import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Input } from "@/components/ui/input";
import { Search, Loader2, Store, X } from "lucide-react";

/**
 * AdminHeaderBusinessSearch — a compact search bar rendered in the admin
 * header. Debounces the query, hits /api/admin/businesses (existing
 * endpoint), and shows a dropdown of matches. Clicking one opens that
 * business's public profile in a new tab so the admin can edit/delete
 * without losing their place in the admin panel.
 */
export default function AdminHeaderBusinessSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const wrapRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const runSearch = useCallback(async (needle) => {
    if (!needle || needle.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/businesses`, {
        params: { search: needle.trim(), limit: 20 },
      });
      setResults(r.data?.businesses || r.data || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => runSearch(q), 250);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [q, runSearch]);

  const openBiz = (biz) => {
    setOpen(false);
    setQ("");
    setResults([]);
    // Admin edit/delete buttons live on the public profile page.
    // Same tab so they can act quickly; browser back returns to admin.
    navigate(`/business/${biz.business_id}`);
  };

  return (
    <div ref={wrapRef} className="relative w-full sm:max-w-md" data-testid="admin-header-search">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <Input
          placeholder="Search businesses by name, city, category…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          className="pl-9 pr-9 bg-white/90 focus:bg-white text-tbc-navy placeholder:text-gray-500"
          data-testid="admin-header-search-input"
        />
        {q && (
          <button
            onClick={() => { setQ(""); setResults([]); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-tbc-navy p-1"
            aria-label="Clear search"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-2xl border border-gray-200 max-h-96 overflow-y-auto z-50">
          {loading ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Searching…
            </div>
          ) : results.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">No matches for &ldquo;{q}&rdquo;</div>
          ) : (
            results.map((b) => (
              <button
                key={b.business_id}
                onClick={() => openBiz(b)}
                className="w-full text-left p-3 hover:bg-tbc-gold/10 border-b border-gray-50 last:border-0 flex items-center gap-3"
                data-testid={`admin-header-search-result-${b.business_id}`}
              >
                {b.logo_url ? (
                  <img
                    src={b.logo_url}
                    alt=""
                    className="w-9 h-9 rounded-lg object-cover flex-shrink-0"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                ) : (
                  <div className="w-9 h-9 rounded-lg bg-tbc-navy/10 text-tbc-navy flex items-center justify-center flex-shrink-0">
                    <Store className="w-4 h-4" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-tbc-navy truncate">{b.name}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {b.category}
                    {b.city && b.state ? ` · ${b.city}, ${b.state}` : ""}
                    {b.is_verified ? " · ✓ verified" : ""}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
